/**
 * Reset-password screen, reached from the link in the reset email.
 *
 * The token arrives as ?token=… and is passed straight through to
 * /auth/reset-password. Tokens are single-use and expire after an hour, so an
 * expired or already-used link surfaces the backend's message and offers a path
 * back to request a fresh one.
 *
 * Client-side validation uses the shared validateNewPassword helper so this
 * screen cannot drift from the Account page, the forced-reset screen, or the
 * server. The server remains the authority.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/apiService';
import { validateNewPassword, PASSWORD_REQUIREMENTS_TEXT } from '../utils/passwordPolicy';
import cegLogo from '../assets/CEG Logo.svg';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    const bad = validateNewPassword(password);
    if (bad) { setError(bad); return; }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        (err?.response?.status === 429
          ? 'Too many attempts. Please wait a while and try again.'
          : 'Could not reset your password. Please request a new link.')
      );
    } finally {
      setLoading(false);
    }
  }

  // No token in the URL at all — the link was mangled or visited directly.
  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoWrap}><img src={cegLogo} alt="CEG" style={styles.logoImg} /></div>
          <h1 style={styles.title}>Link incomplete</h1>
          <p style={styles.subtitle}>
            This reset link is missing its token. It may have been broken across
            two lines by your email client — try copying the whole URL.
          </p>
          <Link to="/forgot-password" style={styles.backBtn}>Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}><img src={cegLogo} alt="CEG" style={styles.logoImg} /></div>

        {done ? (
          <>
            <h1 style={styles.title}>Password updated</h1>
            <p style={styles.subtitle}>Taking you to the sign-in page…</p>
            <Link to="/login" style={styles.backBtn}>Sign in now</Link>
          </>
        ) : (
          <>
            <h1 style={styles.title}>Choose a new password</h1>
            <p style={styles.subtitle}>
              {PASSWORD_REQUIREMENTS_TEXT}.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <div style={styles.labelRow}>
                  <label style={styles.label} htmlFor="password">New password</label>
                  <button type="button" style={styles.showHideBtn} onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="••••••••••••"
                  required
                  autoFocus
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="confirm">Confirm new password</label>
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  style={styles.input}
                  placeholder="••••••••••••"
                  required
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <button type="submit" style={styles.submitBtn} disabled={loading || !password || !confirm}>
                {loading ? 'Saving…' : 'Set new password'}
              </button>
            </form>

            <p style={styles.applyLink}>
              <Link to="/forgot-password" style={styles.link}>Request a new link</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-mint)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-6)',
  },
  card: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-modal)',
    padding: 'var(--space-10)',
    width: '100%',
    maxWidth: 400,
  },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 30, fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', marginBottom: 'var(--space-1)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.75, textAlign: 'center', marginBottom: 'var(--space-8)', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  showHideBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55, padding: 0 },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  input: {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--color-mint-dark)',
    background: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-forest)',
    outline: 'none',
  },
  error: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', textAlign: 'center', margin: 0 },
  submitBtn: {
    marginTop: 'var(--space-2)',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
  },
  backBtn: {
    display: 'block',
    textAlign: 'center',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    textDecoration: 'none',
  },
  applyLink: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'center', marginTop: 'var(--space-6)', opacity: 0.75 },
  link: { color: 'var(--color-forest)', fontWeight: 700 },
};
